import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* eslint "no-param-reassign": "warn" */
import { normalize } from 'normalizr';
import omit from 'lodash/omit';
import { bankEntrySchema, bankSchema, interactionTypeSchema, itemAnalysisSchema, itemSchema, qtiImportSchema, quizAnalysisSchema, quizEntrySchema, quizEntriesSchema, quizSchema, quizSessionEventSchema, quizSessionResultSchema, quizSessionSchema, resumeDataSchema, shareBankSchema, sessionItemResultSchema, sessionItemSchema, stimulusSchema } from './schema';
import getPersistenceActions from './getPersistenceActions';
import { loadResumeData } from '../actions/taking';

// ===========
//   HELPERS
// ===========

var handleSingleObject = function handleSingleObject(data, schema) {
  var normalizedData = normalize(data, schema);
  return getPersistenceActions(normalizedData);
};
var handleArray = function handleArray(data, schema) {
  var normalizedData = normalize(data, [schema]);
  return getPersistenceActions(normalizedData);
};
var setQuizSessionKeys = function setQuizSessionKeys(quizSessionId, itemToKey) {
  itemToKey.quizSessionId = quizSessionId;
  itemToKey.id = "".concat(quizSessionId, "_").concat(itemToKey.position);
};
var setQuizSessionKeysForList = function setQuizSessionKeysForList(quizSessionId, listOfItems) {
  return listOfItems.forEach(function (itemToKey) {
    return setQuizSessionKeys(quizSessionId, itemToKey);
  });
};
var omitDeprecatedQSData = function omitDeprecatedQSData(quizSessionData) {
  return omit(quizSessionData, ['resume_data', 'session_items', 'session_item_responses']);
};
var quizEntriesWithInteractionTypes = function quizEntriesWithInteractionTypes(quizEntriesData) {
  return quizEntriesData.map(function (quizEntry) {
    var sampleItemsInteractionTypes = [];
    quizEntry.sample_items && quizEntry.sample_items.forEach(function (item) {
      var itemInteractionType = item.entry && item.entry.interaction_type;
      if (itemInteractionType && !sampleItemsInteractionTypes.filter(function (i) {
        return i.id === itemInteractionType.id;
      }).length) {
        sampleItemsInteractionTypes.push(itemInteractionType);
      }
    });
    return _objectSpread(_objectSpread({}, quizEntry), {}, {
      interaction_types: sampleItemsInteractionTypes
    });
  });
};

// ============
//   HANDLERS
// ============

// these handlers are called in the success callback of an api call

// - normalize the data (via normaliz & schema)
// - get actions based on that data (via getPersistenceActions)
// return an array of those actions
// - adds id attribute to objects that need a uniq key in the store

// ========= Single Object Handlers =========

export var handleQuizResponse = function handleQuizResponse(quizData) {
  return handleSingleObject(quizData, quizSchema);
};
export var handleQuizAnalysisResponse = function handleQuizAnalysisResponse(quizAnalysisData) {
  return handleSingleObject(quizAnalysisData, quizAnalysisSchema);
};
export var handleQuizSessionResponse = function handleQuizSessionResponse(quizSessionData) {
  return handleSingleObject(omitDeprecatedQSData(quizSessionData), quizSessionSchema);
};
export var handleBankResponse = function handleBankResponse(bankData) {
  return handleSingleObject(bankData, bankSchema);
};
export var handleShareBankResponse = function handleShareBankResponse(shareBankData) {
  return handleSingleObject(shareBankData, shareBankSchema);
};
export var handleBankEntryResponse = function handleBankEntryResponse(bankEntryData) {
  return handleSingleObject(bankEntryData, bankEntrySchema);
};
export var handleQuizEntryResponse = function handleQuizEntryResponse(quizEntryData) {
  return handleSingleObject(quizEntryData, quizEntrySchema);
};

// ========= Array Handlers =========

export var handleQtiImportsResponse = function handleQtiImportsResponse(importsData) {
  return handleArray(importsData, qtiImportSchema);
};
export var handleQuizSessionEventsResponse = function handleQuizSessionEventsResponse(quizSessionEventsData) {
  return handleArray(quizSessionEventsData, quizSessionEventSchema);
};
export var handleItemAnalysesResponse = function handleItemAnalysesResponse(itemAnalysisData) {
  return handleArray(itemAnalysisData, itemAnalysisSchema);
};
export var handleItemsResponse = function handleItemsResponse(itemsData) {
  return handleArray(itemsData, itemSchema);
};
export var handleStimuliResponse = function handleStimuliResponse(stimuliData) {
  return handleArray(stimuliData, stimulusSchema);
};
export var handleInteractionTypesResponse = function handleInteractionTypesResponse(interactionTypesData) {
  return handleArray(interactionTypesData, interactionTypeSchema);
};
export var handleQuizEntriesResponse = function handleQuizEntriesResponse(quizEntriesData) {
  return handleArray(quizEntriesWithInteractionTypes(quizEntriesData), quizEntriesSchema);
};
export var handleSessionItemsResponse = function handleSessionItemsResponse(sessionItemsData, quizSessionId) {
  setQuizSessionKeysForList(quizSessionId, sessionItemsData);
  return handleArray(sessionItemsData, sessionItemSchema);
};
export var handleSessionItemResultsResponse = function handleSessionItemResultsResponse(sessionItemResultsData, quizSessionId) {
  setQuizSessionKeysForList(quizSessionId, sessionItemResultsData);
  return handleArray(sessionItemResultsData, sessionItemResultSchema);
};
export var handleResumeDataResponse = function handleResumeDataResponse(resumeData, quizSessionId) {
  setQuizSessionKeysForList(quizSessionId, resumeData);
  if (resumeData.length === 0) {
    return loadResumeData(resumeData);
  }
  return handleArray(resumeData, resumeDataSchema);
};
export var handleQuizSessionResultsResponse = function handleQuizSessionResultsResponse(quizSessionResultsData) {
  return handleArray(quizSessionResultsData, quizSessionResultSchema);
};
export var handleQuizSessionsResponse = function handleQuizSessionsResponse(quizSessionsData) {
  return handleArray(quizSessionsData.map(omitDeprecatedQSData), quizSessionSchema);
};
export var handleBanksResponse = function handleBanksResponse(banksData) {
  return handleArray(banksData, bankSchema);
};
export var handleShareBanksResponse = function handleShareBanksResponse(shareBankData) {
  return handleArray(shareBankData, shareBankSchema);
};
export var handleBankEntriesResponse = function handleBankEntriesResponse(bankEntriesData) {
  return handleArray(bankEntriesData, bankEntrySchema);
};